import {
  AUDIT_LINKS,
  INTEGRATION_LINKS,
  RESEARCH_LINKS,
  STANDARD_VIEWS,
  WORKSPACE_LINKS,
  type NavDestination,
} from "@/lib/nav/registry";
import {
  clientDataProviderPermission,
  permissionValue,
  type ClientPermissionKey,
  type ClientUserPermissions,
} from "@/lib/auth/client-permissions";
import { clientDashboardNavHasData } from "@/components/sidebar/client-nav-visibility";
import {
  publishingCalendarPath,
  publishingContentPath,
  publishingApprovalsPath,
  sitemapListPath,
  mediaPath,
} from "@/lib/publishing/paths";

const relative = (path: string) => path.replace(/^\/clients\/[^/]+\//, "");
const publishing: NavDestination[] = [
  { key: "calendar", path: relative(publishingCalendarPath("company")) },
  {
    key: "content",
    path: relative(publishingContentPath("company")),
    permission: "publishing.content.view",
  },
  { key: "approvals", path: relative(publishingApprovalsPath("company")) },
  {
    key: "sitemap_planner",
    path: relative(sitemapListPath("company")),
    permission: "sitemaps.view",
  },
  {
    key: "media_library",
    path: relative(mediaPath("company")),
    permission: "media.view",
  },
].map((entry) => ({
  ...entry,
  label: entry.key,
  iconKey: "grid",
  group: "workspace",
})) as NavDestination[];
const destinations = [
  ...STANDARD_VIEWS,
  ...INTEGRATION_LINKS,
  ...AUDIT_LINKS,
  ...RESEARCH_LINKS,
  ...WORKSPACE_LINKS,
  ...publishing,
].sort((a, b) => b.path.length - a.path.length);

/** Keep the tool's landing page, never a source company's record or print URL. */
export function companySwitchDestination(
  path: string,
): NavDestination | undefined {
  return destinations.find(
    (d) => path === d.path || path.startsWith(`${d.path}/`),
  );
}

export function companySwitchHref(
  targetId: string,
  pathname: string,
  search: { get: (key: string) => string | null },
): string {
  const match = pathname.match(/^\/clients\/[^/]+\/(.+)$/);
  const destination = match ? companySwitchDestination(match[1]) : undefined;
  const params = new URLSearchParams();
  if (destination) params.set("switchTo", destination.path);
  for (const key of destination
    ? ["range", "compare", "period", "tab"]
    : ["range", "compare"]) {
    const value = search.get(key);
    if (value) params.set(key, value);
  }
  return `/clients/${targetId}${params.size ? `?${params}` : ""}`;
}

export interface CompanySwitchContext {
  providers: Set<string>;
  isStaff: boolean;
  permissions: ClientUserPermissions | null;
  socialEnabled: boolean;
  sitemapsEnabled: boolean;
}

/** Only registered destinations are accepted, even if switchTo is hand-edited. */
export function availableCompanySwitchPath(
  path: string | undefined,
  context: CompanySwitchContext,
): string | null {
  const d = path
    ? destinations.find((entry) => entry.path === path)
    : undefined;
  if (!d) return null;
  const { providers, isStaff, permissions } = context;
  const can = (permission: ClientPermissionKey) =>
    isStaff || (!!permissions && permissionValue(permissions, permission));
  if (d.staffOnly && !isStaff) return null;
  if (d.provider && !providers.has(d.provider)) return null;
  if (
    d.provider &&
    !can(
      clientDataProviderPermission(d.provider) ??
        (`providers.${d.provider}.view` as ClientPermissionKey),
    )
  )
    return null;
  if (d.permission && !can(d.permission)) return null;
  if (
    !isStaff &&
    (permissions?.hidden_views?.includes(d.key) ||
      (d.path.startsWith("pulse") &&
        permissions?.hidden_views?.includes("pulse")) ||
      (d.provider && permissions?.hidden_providers?.includes(d.provider)))
  )
    return null;
  if (
    d.group === "dashboard" &&
    !isStaff &&
    !clientDashboardNavHasData(d.key, providers)
  )
    return null;
  if (d.key === "site-vitals" && !can("site_vitals.view")) return null;
  if (publishing.includes(d)) {
    if (d.key === "sitemap_planner" && !context.sitemapsEnabled) return null;
    if (!context.socialEnabled && !context.sitemapsEnabled) return null;
    if (
      d.key !== "media_library" &&
      !(context.socialEnabled && can("social.view")) &&
      !(context.sitemapsEnabled && can("sitemaps.view"))
    )
      return null;
  }
  return d.path;
}
